import { MapUtils } from '../../../utility/map-utils.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';

import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { cloneData, isSameData } from '../../dto/data-model-utils.js';

import { BusinessModuleRef } from '../business-module-ref.js';
import { VersionRange } from '../../core/version-range.js';

export enum CampaignStatus { Active = 'Active', Inactive = 'Inactive' }

export class Campaign extends DataModel {
  static readonly TYPE_ID: string = '571545cf14744688989b5c7631cf6225';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly campaignId: string,
    public readonly campaignName: string,
    public readonly adAccountId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly status: CampaignStatus,
    public readonly timedAtHour: number,
    public readonly markedForDeactivation: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return Campaign.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(Campaign.SCHEMA_VERSION);
  }

  static create(args: {
    campaignId: string,
    campaignName: string,
    adAccountId: string,
    businessRef: BusinessModuleRef,
    status: CampaignStatus,
    timedAtHour: number,
    markedForDeactivation?: boolean,
  }): Campaign {
    return new Campaign(
        args.campaignId,
        args.campaignName,
        args.adAccountId,
        args.businessRef,
        args.status,
        args.timedAtHour,
        args.markedForDeactivation ?? false
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): Campaign {
    return Campaign.create({
      campaignId: MapUtils.getString(map, 'campaignId'),
      campaignName: MapUtils.getString(map, 'campaignName'),
      adAccountId: MapUtils.getString(map, 'adAccountId'),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      status: toEnumFromString(MapUtils.getString(map, 'status'), CampaignStatus),
      timedAtHour: MapUtils.getInteger(map, 'timedAtHour'),
      markedForDeactivation: MapUtils.getBoolean(map, 'markedForDeactivation'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'campaignId': this.campaignId,
      'campaignName': this.campaignName,
      'adAccountId': this.adAccountId,
      'businessRef': this.businessRef.encode(),
      'status': fromEnumAsString<CampaignStatus>(this.status),
      'timedAtHour': this.timedAtHour,
      'markedForDeactivation': this.markedForDeactivation,
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    campaignId = this.campaignId,
    campaignName = this.campaignName,
    adAccountId = this.adAccountId,
    businessRef = cloneData(this.businessRef),
    status = this.status,
    timedAtHour = this.timedAtHour,
    markedForDeactivation = this.markedForDeactivation,
  }: {
    campaignId?: string,
    campaignName?: string,
    adAccountId?: string,
    businessRef?: BusinessModuleRef,
    status?: CampaignStatus,
    timedAtHour?: number,
    markedForDeactivation?: boolean
  } = {}): this {
    return Campaign.create({
      campaignId,
      campaignName,
      adAccountId,
      businessRef,
      status,
      timedAtHour,
      markedForDeactivation,
    }) as this;
  }

  isSame(other: Campaign): boolean {
    if(other === this) {
      return true;
    }

    return other.campaignId === this.campaignId &&
      other.campaignName === this.campaignName &&
      other.adAccountId === this.adAccountId &&
      isSameData(other.businessRef, this.businessRef) &&
      other.status === this.status &&
      other.timedAtHour === this.timedAtHour &&
      other.markedForDeactivation === this.markedForDeactivation;
  }
}

export class CampaignBuilder extends DataModelBuilder<Campaign> {
  build(context: SerializationContext, map: SerializedData): Campaign {
    return Campaign.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(Campaign.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(Campaign.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version: number = versionRange.to;

    // Added new field adAccountId
    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'adAccountId', 'ZzyEI2MyQqtu2lBdKPBp');

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
